import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/features/auth/AuthProvider';
import { tourConfigs, type TourStep } from '@/config/tourSteps';
import { TourSpotlight } from './TourSpotlight';

interface TourContextValue {
    isActive: boolean;
    currentStep: number;
    currentStepData: TourStep | null;
    totalSteps: number;
    tourId: string | null;
    startTour: (tourId: string) => void;
    nextStep: () => void;
    prevStep: () => void;
    skipTour: () => void;
    completeTour: () => void;
    restartTour: (tourId: string) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export const useTourContext = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTourContext must be used within GuidedTourProvider');
    }
    return context;
};

interface GuidedTourProviderProps {
    children: ReactNode;
}

export const GuidedTourProvider: React.FC<GuidedTourProviderProps> = ({ children }) => {
    const { currentUser } = useAuth();
    const [isActive, setIsActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [tourId, setTourId] = useState<string | null>(null);

    const currentTour = tourId ? tourConfigs[tourId] : null;
    const steps = currentTour?.steps || [];
    const currentStepData = steps[currentStep] || null;

    const markTourComplete = useCallback(async (completed: boolean = true) => {
        if (!currentUser?.uid || !tourId) return;

        // Optimistic Local Storage update
        try {
            localStorage.setItem(`tour_completed_${tourId}`, String(completed));
        } catch (e) {
            // ignore
        }

        try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
                [`tours.${tourId}`]: {
                    completed,
                    completedAt: completed ? serverTimestamp() : null
                },
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error updating tour status:', err);
        }
    }, [currentUser?.uid, tourId]);

    const startTour = useCallback((id: string) => {
        if (tourConfigs[id]) {
            setTourId(id);
            setCurrentStep(0);
            setIsActive(true);
        }
    }, []);

    const restartTour = useCallback((id: string) => {
        // Reset completion status and start fresh
        if (tourConfigs[id] && currentUser?.uid) {
            updateDoc(doc(db, 'users', currentUser.uid), {
                [`tours.${id}`]: {
                    completed: false,
                    completedAt: null
                }
            }).then(() => {
                startTour(id);
            });
        }
    }, [currentUser?.uid, startTour]);

    const nextStep = useCallback(() => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            // Last step - complete the tour
            markTourComplete(true);
            setIsActive(false);
            setCurrentStep(0);
            setTourId(null);
        }
    }, [currentStep, steps.length, markTourComplete]);

    const prevStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    const skipTour = useCallback(() => {
        markTourComplete(true);
        setIsActive(false);
        setCurrentStep(0);
        setTourId(null);
    }, [markTourComplete]);

    const completeTour = useCallback(() => {
        markTourComplete(true);
        setIsActive(false);
        setCurrentStep(0);
        setTourId(null);
    }, [markTourComplete]);

    // Auto-start tour for newly approved parents
    useEffect(() => {
        if (!currentUser) {
            return;
        }

        // Check if parent, active, and tour not completed (Firestore OR LocalStorage)
        // const isParent = currentUser.role === 'parent';
        // const isActive = currentUser.status === 'active';
        // const tourData = currentUser.tours?.['parent-onboarding'];

        // Check localStorage as fallback/immediate cache
        // const localCompleted = localStorage.getItem('tour_completed_parent-onboarding') === 'true';
        // const tourCompleted = tourData?.completed === true || localCompleted;

        // FIXED: Disabled by request (rendering issues)
        const shouldStartTour = false; // isParent && isActive && !tourCompleted;

        if (shouldStartTour) {
            // Small delay to ensure dashboard is loaded
            const timer = setTimeout(() => {
                startTour('parent-onboarding');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [currentUser?.uid, currentUser?.role, currentUser?.status, currentUser?.tours, startTour]);

    const value: TourContextValue = {
        isActive,
        currentStep,
        currentStepData,
        totalSteps: steps.length,
        tourId,
        startTour,
        nextStep,
        prevStep,
        skipTour,
        completeTour,
        restartTour
    };

    return (
        <TourContext.Provider value={value}>
            {children}
            {isActive && currentStepData && (
                <TourSpotlight
                    step={currentStepData}
                    currentStep={currentStep}
                    totalSteps={steps.length}
                    onNext={nextStep}
                    onPrev={prevStep}
                    onSkip={skipTour}
                />
            )}
        </TourContext.Provider>
    );
};
